/**
 * Application root: owns the world canvas (behind the interface), poster/fallback, the gallery and
 * world chrome, the player, settings, auto-hiding controls and zoom input.
 */
import { lazy, Suspense, useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { assetUrl } from './assets';
import { AppController, GRAPHICS_GAVE_UP } from './controller';
import { Gallery } from './Gallery';
import { Player } from './Player';
import { SettingsSheet } from './SettingsSheet';
import { Toast } from './ui/controls';
import { WorldChrome } from './WorldChrome';

const DevPanel = lazy(() => import('../framework/dev/DevPanel'));

const HIDE_AFTER_MS = 3500;

export function App() {
  const [app] = useState(() => new AppController());
  const snap = useSyncExternalStore(app.subscribe, app.getSnapshot);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const uiRef = useRef<HTMLDivElement>(null);
  const [chrome, setChrome] = useState(true);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [fullscreen, setFullscreen] = useState(false);
  const hideTimer = useRef(0);

  // StrictMode-safe engine lifecycle: development double-mounting cancels the deferred dispose
  // instead of tearing down and re-creating the GPU context.
  const pendingDispose = useRef(0);
  const initialised = useRef(false);
  useEffect(() => {
    if (pendingDispose.current) {
      window.clearTimeout(pendingDispose.current);
      pendingDispose.current = 0;
    }
    if (!initialised.current) {
      initialised.current = true;
      void app.init(canvasRef.current!);
    }
    return () => {
      pendingDispose.current = window.setTimeout(() => app.dispose(), 0);
    };
  }, [app]);

  // After a graphics failure the controller asks for a fresh canvas (a canvas whose context was
  // lost for good cannot get another); the keyed <canvas> below is new, so attach to it.
  useEffect(() => {
    if (snap.canvasGeneration > 0 && canvasRef.current) void app.attach(canvasRef.current, snap.canvasGeneration);
  }, [app, snap.canvasGeneration]);

  useEffect(() => {
    document.documentElement.dataset.reducedMotion = String(snap.reducedMotion);
  }, [snap.reducedMotion]);

  useEffect(() => {
    const onFs = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFs);
    return () => document.removeEventListener('fullscreenchange', onFs);
  }, []);

  // ----- Auto-hiding chrome (immersive view only) -------------------------------------
  const immersive = snap.view === 'world';
  const holdOpen = useCallback(() => {
    const ui = uiRef.current;
    if (settingsOpen) return true;
    if (document.querySelector('.gbr-popover, .gbr-sheet')) return true;
    if (document.querySelector('.gbr-player[data-open="true"]')) return true;
    if (ui && ui.contains(document.activeElement) && document.activeElement !== document.body) return true;
    if (ui?.querySelector(':hover') && matchMedia('(hover: hover)').matches) return true;
    return false;
  }, [settingsOpen]);

  const scheduleHide = useCallback(() => {
    window.clearTimeout(hideTimer.current);
    if (!immersive) return;
    const schedule = () => {
      hideTimer.current = window.setTimeout(() => {
        if (holdOpen()) schedule();
        else setChrome(false);
      }, HIDE_AFTER_MS);
    };
    schedule();
  }, [immersive, holdOpen]);

  const reveal = useCallback(() => {
    setChrome(true);
    scheduleHide();
  }, [scheduleHide]);

  useEffect(() => {
    scheduleHide();
    return () => window.clearTimeout(hideTimer.current);
  }, [scheduleHide]);

  useEffect(() => {
    const onMove = (e: PointerEvent) => {
      if (e.pointerType === 'mouse') reveal();
    };
    const onKey = (e: KeyboardEvent) => {
      reveal();
      if (!immersive || e.defaultPrevented) return;
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT') return;
      if (e.key === '+' || e.key === '=') app.zoomBy(0.97);
      else if (e.key === '-' || e.key === '_') app.zoomBy(1.03);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('keydown', onKey);
    document.addEventListener('focusin', reveal);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('keydown', onKey);
      document.removeEventListener('focusin', reveal);
    };
  }, [reveal, immersive, app]);

  // Canvas: tap toggles chrome on touch; wheel and pinch zoom within the bounded range.
  useEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const pointers = new Map<number, { x: number; y: number }>();
    let pinch = 0;
    let downAt = 0;
    const onDown = (e: PointerEvent) => {
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      downAt = performance.now();
      if (pointers.size === 2) {
        const [a, b] = [...pointers.values()];
        pinch = Math.hypot(a.x - b.x, a.y - b.y);
      }
    };
    const onMovePtr = (e: PointerEvent) => {
      if (!pointers.has(e.pointerId)) return;
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointers.size === 2 && pinch > 0) {
        const [a, b] = [...pointers.values()];
        const d = Math.hypot(a.x - b.x, a.y - b.y);
        app.zoomBy(pinch / d);
        pinch = d;
      }
    };
    const onUp = (e: PointerEvent) => {
      const wasTap = pointers.size === 1 && performance.now() - downAt < 300;
      pointers.delete(e.pointerId);
      if (pointers.size < 2) pinch = 0;
      if (wasTap && e.pointerType !== 'mouse' && snap.view === 'world') {
        if (chrome) {
          window.clearTimeout(hideTimer.current);
          setChrome(false);
        } else reveal();
      } else if (wasTap) reveal();
    };
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      app.zoomBy(Math.exp(e.deltaY * 0.0008));
      reveal();
    };
    c.addEventListener('pointerdown', onDown);
    c.addEventListener('pointermove', onMovePtr);
    c.addEventListener('pointerup', onUp);
    c.addEventListener('pointercancel', onUp);
    c.addEventListener('wheel', onWheel, { passive: false });
    return () => {
      c.removeEventListener('pointerdown', onDown);
      c.removeEventListener('pointermove', onMovePtr);
      c.removeEventListener('pointerup', onUp);
      c.removeEventListener('pointercancel', onUp);
      c.removeEventListener('wheel', onWheel);
    };
  }, [app, chrome, reveal, snap.view, snap.canvasGeneration]);

  const entries = app.catalog.map((c) => c.manifest);
  // Also while the graphics context is lost (iOS takes it from a page in the background): the
  // poster, not an empty dark stage, until the picture is restored or rebuilt.
  const posterVisible = snap.phase !== 'ready' || snap.graphicsLost;
  const showToast = useCallback((m: string) => setToast(m), []);
  // Stable, so the toast's timer is not restarted by every snapshot update.
  const dismissToast = useCallback(() => {
    setToast(null);
    app.clearNotice();
  }, [app]);

  return (
    <>
      <a className="gbr-skip" href="#gbr-controls">
        Skip to controls
      </a>
      <div className="gbr-stage" aria-hidden="true">
        <canvas key={snap.canvasGeneration} ref={canvasRef} className="gbr-canvas" />
        {snap.manifest && <img className="gbr-poster" src={assetUrl(snap.manifest.preview.poster)} alt="" data-hidden={!posterVisible} style={{ background: snap.manifest.preview.palette[0] }} />}
      </div>
      {snap.phase === 'unsupported' && snap.manifest && (
        <div className="gbr-fallback" role="note">
          <p className="gbr-fallback-note">
            This browser can’t show living worlds (WebGL 2 is unavailable or disabled), so this is a still picture of {snap.manifest.title}, not the world itself. A current version of
            Chrome, Edge, Firefox or Safari will show it moving.
          </p>
        </div>
      )}
      {snap.phase === 'error' && snap.view === 'world' && snap.manifest && (
        <div className="gbr-fallback" role="note">
          <p className="gbr-fallback-note">
            {snap.error === GRAPHICS_GAVE_UP
              ? `The graphics stopped responding, so this is a still picture of ${snap.manifest.title}. Reloading the page will bring it back to life; a lighter detail level in Settings may help it stay that way.`
              : `${snap.manifest.title} stopped (${snap.error ?? 'an unknown error'}). Reloading the page will start it again.`}
          </p>
        </div>
      )}
      <div className="gbr-scrim-top" data-visible={chrome} style={{ opacity: chrome || !immersive ? 1 : 0 }} />
      <div className="gbr-scrim-bottom" style={{ opacity: chrome || !immersive ? 1 : 0 }} />
      <main ref={uiRef} id="gbr-controls" className="gbr-ui" data-chrome={chrome || !immersive ? 'shown' : 'hidden'} aria-label={snap.manifest ? `${snap.manifest.title} — Glass Box Relaxation` : 'Glass Box Relaxation'}>
        {snap.view === 'gallery' ? (
          <Gallery app={app} snap={snap} entries={entries} />
        ) : (
          <WorldChrome app={app} snap={snap} onOpenSettings={() => setSettingsOpen(true)} onToast={showToast} isFullscreen={fullscreen} />
        )}
        <Player app={app} chromeVisible={chrome} />
      </main>
      <SettingsSheet app={app} snap={snap} open={settingsOpen} onClose={() => setSettingsOpen(false)} />
      <Toast message={toast ?? snap.notice} onDone={dismissToast} />
      {app.devTools && (
        <Suspense fallback={null}>
          <DevPanel app={app} />
        </Suspense>
      )}
    </>
  );
}
